import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CRow,
  CFormInput,
  CForm,
  CSpinner,
  CAlert,
  CContainer,
} from '@coreui/react'
import { cilChevronBottom, cilChevronTop } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import { useNavigate } from 'react-router-dom'
//import WidgetTop3 from '../widgets/WidgetTop3'
import WidgetTop3Short from '../widgets/WidgetTop3Short'
import { useSwipeable } from 'react-swipeable'
import { addEvent } from '../../utils/calendar'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import updateScore from '../../utils/updateScore'
import getReminderNextSteps from '../../utils/reminderPlanner'
import apiClient from '../../services/apiClient'
import RecommendationCard, { RecommendationSkeleton } from './components/RecommendationCard'
import useAccountContext from '../../hooks/useAccountContext'
import { updateReminderState, postponeReminder } from '../../services/remindersService'
import { logError, logEvent } from '../../utils/telemetry'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../../utils/i18nConfig'

const Risks = () => {
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(20)
  const [error, setError] = useState(null)
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0)
  const { t, i18n } = useTranslation()
  const accountContext = useAccountContext()
  const fetchControllerRef = useRef(null)
  const lastReminderRef = useRef(null)
  const lastSupplierRef = useRef({
    supplier_ID: null,
    supplier_contact_tel: null,
    supplier_name: null,
  })
  const lastExpectedMonthRef = useRef(null)
  const listContainerRef = useRef(null)
  const handlers = useSwipeable({
    onSwipedLeft: () => console.log('Swiped Left!'),
    onSwipedRight: () => console.log('Swiped Right!'),
    onSwipedUp: () => console.log('Swiped Up!'),
    onSwipedDown: () => console.log('Swiped Down!'),
  })

  useEffect(() => {}, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem('i18nextLng')
    const normalized = normalizeLanguage(stored || i18n.language)
    if (normalized && normalized !== i18n.language) {
      i18n.changeLanguage(normalized)
    }
  }, [i18n])

  useEffect(() => {
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) =>
        console.warn('Unable to hide splash screen', error),
      )
    }
  }, [])

  const removeRecommendation = useCallback((reminderId) => {
    setRecommendations((prev) =>
      prev.filter((item) => String(item.reminder_ID) !== String(reminderId)),
    )
  }, [])

  const patchRecommendation = useCallback((reminderId, updates) => {
    setRecommendations((prev) =>
      prev.map((item) =>
        String(item.reminder_ID) === String(reminderId) ? { ...item, ...updates } : item,
      ),
    )
  }, [])

  const readLocalStorage = useCallback((key) => {
    if (typeof window === 'undefined') return ''
    return localStorage.getItem(key) || ''
  }, [])

  const selectReminder = useCallback((reminderId) => {
    if (typeof window === 'undefined') return
    if (lastReminderRef.current === reminderId) return
    lastReminderRef.current = reminderId
    localStorage.setItem('taskid', reminderId)
  }, [])

  const setSupplierContext = useCallback((recommendation) => {
    if (typeof window === 'undefined' || !recommendation) return
    const next = {
      supplier_ID: recommendation.supplier_ID || '',
      supplier_contact_tel: recommendation.supplier_contact_tel || '',
      supplier_name: recommendation.supplier_name || '',
    }
    if (
      lastSupplierRef.current.supplier_ID === next.supplier_ID &&
      lastSupplierRef.current.supplier_contact_tel === next.supplier_contact_tel &&
      lastSupplierRef.current.supplier_name === next.supplier_name
    ) {
      return
    }
    lastSupplierRef.current = next
    localStorage.setItem('supplierid', next.supplier_ID)
    localStorage.setItem('suppliertel', next.supplier_contact_tel)
    localStorage.setItem('suppliername', next.supplier_name)
  }, [])

  const setExpectedMonth = useCallback((value) => {
    if (typeof window === 'undefined') return
    if (lastExpectedMonthRef.current === value) return
    lastExpectedMonthRef.current = value
    localStorage.setItem('AImonth', String(value))
  }, [])

  const triggerWidgetRefresh = useCallback(() => {
    setWidgetRefreshKey((prev) => prev + 1)
  }, [])

  const fetchRecommendationsData = useCallback(async () => {
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    const controller = new AbortController()
    fetchControllerRef.current = controller
    setLoading(true)
    setError(null)
    logEvent('recommendations:fetch:start', {
      accountId: accountContext.accountId,
      grpId: accountContext.grpId,
    })

    try {
      const { accountId, grpId, gender } = accountContext
      if (!accountId || !grpId) {
        console.warn('Missing account or group identifier, skipping reminders load.')
        setRecommendations([])
        return
      }
      const response = await apiClient.get(
        buildApiUrl('accountReminders', {
          account_ID: accountId,
          grp_ID: localStorage.getItem('grp_ID'),
          gender: gender || undefined,
        }),
        {
          signal: controller.signal,
        },
      )
      const next = Array.isArray(response.data) ? response.data : []
      setRecommendations(next)
      logEvent('recommendations:fetch:success', { count: next.length })
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        return
      }
      console.error('Error fetching recommendations', err)
      logError('recommendations:fetch:error', err)
      setError('Unable to load activities. Please try again.')
    } finally {
      if (fetchControllerRef.current === controller) {
        setLoading(false)
      }
    }
  }, [accountContext])

  const refreshRecommendations = useCallback(async () => {
    try {
      await fetchRecommendationsData()
    } finally {
      triggerWidgetRefresh()
    }
  }, [fetchRecommendationsData, triggerWidgetRefresh])

  useEffect(() => {
    fetchRecommendationsData()
    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort()
      }
    }
  }, [fetchRecommendationsData])

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query)
    }, 200)

    return () => clearTimeout(handle)
  }, [query])

  useEffect(() => {
    setVisibleCount(20)
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0
    }
  }, [debouncedQuery, recommendations])

  const filteredRecommendations = useMemo(
    () =>
      recommendations.filter((recommendation) =>
        recommendation.Title.toLowerCase().includes(debouncedQuery.toLowerCase()),
      ),
    [recommendations, debouncedQuery],
  )

  const filteredLength = filteredRecommendations.length

  const isInitialLoading = loading && recommendations.length === 0 && !error

  const navigate = useNavigate()
  const performReminderStatusUpdate = useCallback(
    async (state, reminderId) => {
      const effectiveReminderId = reminderId || readLocalStorage('taskid')
      const accountId = accountContext.accountId
      if (!effectiveReminderId || !accountId) {
        console.warn('Missing reminder or account context, state update skipped.')
        return false
      }
      try {
        await updateReminderState({ reminderId: effectiveReminderId, state, accountId })
        return true
      } catch (error) {
        logError('performReminderStatusUpdate', error, { reminderId: effectiveReminderId, state })
        return false
      }
    },
    [accountContext.accountId, readLocalStorage],
  )

  const handleClickSwitchOff = useCallback(
    async (recommendationOrId) => {
      const effectiveId =
        typeof recommendationOrId === 'object'
          ? recommendationOrId?.reminder_ID
          : recommendationOrId || readLocalStorage('taskid')
      const updated = await performReminderStatusUpdate('S', effectiveId)
      if (updated && effectiveId) {
        removeRecommendation(effectiveId)
        logEvent('recommendation:switchOff', { reminderId: effectiveId })
        await refreshRecommendations()
      }
    },
    [performReminderStatusUpdate, readLocalStorage, refreshRecommendations, removeRecommendation],
  )

  const handleClickIgnore = useCallback(
    async (recommendationOrId) => {
      const effectiveId =
        typeof recommendationOrId === 'object'
          ? recommendationOrId?.reminder_ID
          : recommendationOrId || readLocalStorage('taskid')
      const updated = await performReminderStatusUpdate('I', effectiveId)
      if (updated && effectiveId) {
        removeRecommendation(effectiveId)
        logEvent('recommendation:ignored', { reminderId: effectiveId })
        await refreshRecommendations()
      }
    },
    [performReminderStatusUpdate, readLocalStorage, refreshRecommendations, removeRecommendation],
  )

  const handleClickPostpone = useCallback(
    async (reminderId, nextExpectedMonth) => {
      const effectiveReminderId = reminderId || readLocalStorage('taskid')
      const expectedMonth =
        typeof nextExpectedMonth === 'number' && !Number.isNaN(nextExpectedMonth)
          ? nextExpectedMonth
          : Number(readLocalStorage('AImonth')) || nextExpectedMonth

      const accountId = accountContext.accountId

      if (!effectiveReminderId) {
        console.warn('Missing reminder identifier, postpone skipped.')
        return false
      }

      if (!accountId) {
        console.warn('Missing account context, postpone skipped.')
        return false
      }

      if (Number.isFinite(expectedMonth)) {
        setExpectedMonth(expectedMonth)
      }

      try {
        await postponeReminder({
          reminderId: effectiveReminderId,
          expecteddate: Number.isFinite(expectedMonth) ? String(expectedMonth) : '',
          accountId,
        })
        if (Number.isFinite(expectedMonth)) {
          patchRecommendation(effectiveReminderId, { expecteddate: String(expectedMonth) })
        }
        logEvent('recommendation:postponed', {
          reminderId: effectiveReminderId,
          expectedMonth,
        })
        await refreshRecommendations()
        return true
      } catch (error) {
        logError('handleClickPostpone', error, {
          reminderId: effectiveReminderId,
          expectedMonth,
        })
        return false
      }
    },
    [
      accountContext.accountId,
      patchRecommendation,
      readLocalStorage,
      refreshRecommendations,
      setExpectedMonth,
    ],
  )

  const visibleRecommendations = useMemo(
    () => filteredRecommendations.slice(0, visibleCount),
    [filteredRecommendations, visibleCount],
  )

  const handleListScroll = useCallback(
    (event) => {
      const target = event.currentTarget
      if (!target) return
      const threshold = target.scrollHeight - target.clientHeight - 200
      if (target.scrollTop >= threshold) {
        setVisibleCount((prev) =>
          prev < filteredLength ? Math.min(prev + 10, filteredLength) : prev,
        )
      }
    },
    [filteredLength],
  )

  const handleDetailNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      navigate('/detail')
    },
    [navigate, selectReminder],
  )

  const handleSupplierNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      setSupplierContext(recommendation)
      navigate('/suppliers')
    },
    [navigate, selectReminder, setSupplierContext],
  )

  const handleEshopNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      navigate('/eshop')
    },
    [navigate, selectReminder],
  )

  const handleCalendarAdd = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      addEvent(recommendation.Title)
    },
    [selectReminder],
  )

  const handleSwitchOffAction = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      handleClickSwitchOff(recommendation)
    },
    [handleClickSwitchOff, selectReminder],
  )

  const handleIgnoreAction = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      handleClickIgnore(recommendation)
    },
    [handleClickIgnore, selectReminder],
  )

  const handleClickDone = useCallback(
    async (recommendation) => {
      if (!recommendation) return

      const plan = getReminderNextSteps(recommendation)
      if (plan.logMessage) console.log(plan.logMessage)

      const reminderId = recommendation.reminder_ID

      if (plan.postponeMonth !== null) {
        setExpectedMonth(plan.postponeMonth)
      }

      if (plan.shouldPostpone && plan.postponeBeforeComplete) {
        await handleClickPostpone(reminderId, plan.postponeMonth)
      }

      if (plan.shouldComplete) {
        const updated = await performReminderStatusUpdate('D', reminderId)
        if (updated) {
          const points = Number(recommendation.points)
          const groupId = Number(recommendation.grp_ID)
          try {
            await updateScore(
              Number.isFinite(points) ? points : 0,
              Number.isFinite(groupId) ? groupId : 0,
            )
          } catch (error) {
            logError('updateScore', error, { reminderId })
          }
          removeRecommendation(reminderId)
          logEvent('recommendation:completed', { reminderId, points: recommendation.points })
          await refreshRecommendations()
        }
      }

      if (plan.shouldPostpone && !plan.postponeBeforeComplete && !plan.shouldComplete) {
        await handleClickPostpone(reminderId, plan.postponeMonth)
      }
    },
    [
      handleClickPostpone,
      performReminderStatusUpdate,
      refreshRecommendations,
      removeRecommendation,
      setExpectedMonth,
    ],
  )

  const handleDoneAction = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      handleClickDone(recommendation)
    },
    [handleClickDone, selectReminder],
  )

  const handleInnovationSuppliers = useCallback(() => {
    navigate('/suppliersinno')
  }, [navigate])

  const handleInnovationStatus = useCallback(() => {
    navigate('/suppliersinnoprojectstatus')
  }, [navigate])

  if (isInitialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <CSpinner color="primary" variant="grow" size="sm" />
      </div>
    )
  }

  return (
    <div className="risks-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('risks.header.title')} onBack={() => navigate(-1)} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            {error && (
              <CAlert color="danger" className="d-flex justify-content-between align-items-center">
                <span>{error}</span>
                <CButton
                  color="danger"
                  variant="outline"
                  size="sm"
                  onClick={fetchRecommendationsData}
                >
                  {t('risks.actions.retry')}
                </CButton>
              </CAlert>
            )}

            <CCard className="border-0 shadow-sm mb-4">
              <CCardBody className="p-4 p-lg-5">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                  <CForm className="flex-grow-1" onSubmit={(event) => event.preventDefault()}>
                    <CFormInput
                      variant="outlined"
                      placeholder={t('risks.search.placeholder')}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label={t('risks.search.aria')}
                    />
                  </CForm>
                </div>

                <div className="mb-4">
                  <WidgetTop3Short
                    grp_ID={localStorage.getItem('grp_ID')}
                    onDatasetChange={refreshRecommendations}
                    syncToken={widgetRefreshKey}
                  />
                </div>

                <CCard className="bg-light border-0 shadow-sm mb-4">
                  <CCardBody className="py-3 px-4 d-flex align-items-center justify-content-between">
                    <div>
                      <strong>{t('risks.meta.allActivities')}</strong>{' '}
                      <span className="text-medium-emphasis small">
                        {t('risks.meta.totalCount', { count: filteredLength })}
                      </span>
                    </div>
                    <CButton
                      color="primary"
                      variant="outline"
                      className={`risks-toggle-button${visible ? '' : ' risks-toggle-button--pulse'}`}
                      onClick={() => setVisible((prev) => !prev)}
                      aria-label={
                        visible
                          ? t('risks.actions.hideActivities')
                          : t('risks.actions.showActivities')
                      }
                    >
                      <CIcon icon={visible ? cilChevronTop : cilChevronBottom} size="lg" />
                    </CButton>
                  </CCardBody>
                </CCard>

                <div className="small text-medium-emphasis">
                  {visible ? (
                    loading && filteredLength === 0 ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <RecommendationSkeleton key={`skeleton-${index}`} />
                      ))
                    ) : filteredLength > 0 ? (
                      <div
                        ref={listContainerRef}
                        onScroll={handleListScroll}
                        style={{ maxHeight: '70vh', overflowY: 'auto' }}
                      >
                        {visibleRecommendations.map((recommendation) => (
                          <RecommendationCard
                            key={recommendation.reminder_ID}
                            recommendation={recommendation}
                            onDetail={handleDetailNavigation}
                            onSupplier={handleSupplierNavigation}
                            onEshop={handleEshopNavigation}
                            onCalendar={handleCalendarAdd}
                            onSwitchOff={handleSwitchOffAction}
                            onIgnore={handleIgnoreAction}
                            onDone={handleDoneAction}
                            onInnovationSuppliers={handleInnovationSuppliers}
                            onInnovationStatus={handleInnovationStatus}
                          />
                        ))}
                        {loading && filteredLength > 0 && (
                          <div className="text-center py-3">
                            <CSpinner size="sm" variant="grow" color="primary" />
                          </div>
                        )}
                        {visibleCount < filteredLength && (
                          <div className="text-center py-3 text-muted small">
                            {t('risks.meta.scrollHint')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <CCardText className="alert alert-info mb-0">{t('risks.empty')}</CCardText>
                    )
                  ) : (
                    <div className="text-medium-emphasis small">{t('risks.meta.expandHint')}</div>
                  )}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Risks
